import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from "@mui/material";

import { verifyEftpayChemaDonation } from "./donationsApi";

type Phase = "verifying" | "success" | "failed";

/**
 * Landing page OPPWA redirects the shopper to after an EFT PAY COPY&PAY payment.
 * Reads `resourcePath` + `donation_id` from the query string, asks the backend
 * to verify the payment status, and shows the outcome.
 */
export default function EftpayResult() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("verifying");
  const [message, setMessage] = useState<string>("");
  const verified = useRef(false);

  useEffect(() => {
    if (verified.current) return;
    verified.current = true;

    const donationId = Number(params.get("donation_id"));
    const resourcePath = params.get("resourcePath");

    if (!donationId || !resourcePath) {
      setPhase("failed");
      setMessage("Missing payment reference. Please try your donation again.");
      return;
    }

    verifyEftpayChemaDonation(donationId, resourcePath)
      .then((res) => {
        if (res.success) {
          setPhase("success");
          setMessage("Payment confirmed. Thank you for your donation!");
        } else {
          setPhase("failed");
          setMessage(
            `Payment was not completed${res.code ? ` (code ${res.code})` : ""}. Please try again.`,
          );
        }
      })
      .catch((e: Error) => {
        setPhase("failed");
        setMessage(e.message || "Could not verify the payment. Please try again.");
      });
  }, [params]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
        display: "flex",
        alignItems: "center",
        py: { xs: 6, sm: 8 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 2, textAlign: "center" }}>
          {phase === "verifying" && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Confirming your EFT PAY payment...
              </Typography>
            </Box>
          )}

          {phase === "success" && (
            <Alert severity="success" sx={{ justifyContent: "center" }}>
              {message}
            </Alert>
          )}

          {phase === "failed" && (
            <Alert severity="error" sx={{ justifyContent: "center" }}>
              {message}
            </Alert>
          )}

          {phase !== "verifying" && (
            <Button
              variant="contained"
              onClick={() => navigate(-1)}
              sx={{
                mt: 3,
                borderRadius: "999px",
                backgroundColor: "#d97706",
                "&:hover": { backgroundColor: "#b45309" },
              }}
            >
              Back to the memorial page
            </Button>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
